import { flagIcon } from '../../icons';
import { i18nService, onLocaleChange, SupportedLocale, t } from '../../services/i18n';

export class RcLanguageSwitcher extends HTMLElement {
  private disposeLocaleChange?: () => void;
  private handleDocumentClick = (event: MouseEvent) => {
    if (!this.contains(event.target as Node)) {
      this.closeMenu();
    }
  };

  connectedCallback(): void {
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  private render(): void {
    const currentLocale = i18nService.currentLocale;
    const options = i18nService.supportedLocales
      .map((locale) => `
        <button
          type="button"
          class="language-option${locale === currentLocale ? ' active' : ''}"
          data-locale="${locale}"
          role="option"
          aria-selected="${locale === currentLocale}">
          <span class="language-flag" aria-hidden="true">${flagIcon(locale)}</span>
          <span class="language-option-label">${t(`language.${locale}`)}</span>
        </button>
      `)
      .join('');

    this.innerHTML = `
      <div class="language-switcher">
        <span class="language-switcher-label">${t('language.label')}</span>
        <div class="language-switcher-dropdown">
          <button
            type="button"
            class="language-switcher-trigger"
            aria-haspopup="listbox"
            aria-expanded="false">
            <span class="language-flag" aria-hidden="true">${flagIcon(currentLocale)}</span>
            <span class="language-switcher-value">${t(`language.${currentLocale}`)}</span>
            <span class="language-switcher-chevron" aria-hidden="true">▾</span>
          </button>
          <div class="language-switcher-menu" role="listbox" hidden>
            ${options}
          </div>
        </div>
      </div>
    `;

    this.querySelector('.language-switcher-trigger')?.addEventListener('click', () => {
      this.toggleMenu();
    });

    this.querySelectorAll<HTMLElement>('[data-locale]').forEach((option) => {
      option.addEventListener('click', () => {
        i18nService.setLocale(option.dataset.locale as SupportedLocale);
        this.closeMenu();
      });
    });
  }

  private toggleMenu(): void {
    const menu = this.querySelector<HTMLElement>('.language-switcher-menu');
    const trigger = this.querySelector<HTMLElement>('.language-switcher-trigger');
    const isOpen = menu ? !menu.hidden : false;
    if (!menu || !trigger) return;

    menu.hidden = isOpen;
    trigger.setAttribute('aria-expanded', String(!isOpen));
    this.classList.toggle('is-open', !isOpen);
  }

  private closeMenu(): void {
    const menu = this.querySelector<HTMLElement>('.language-switcher-menu');
    const trigger = this.querySelector<HTMLElement>('.language-switcher-trigger');
    if (!menu || !trigger) return;

    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }
}

customElements.define('rc-language-switcher', RcLanguageSwitcher);
